/**
 * Operations summary — the per-phase count contract behind the
 * clickable stat-card row at the top of Operations (the portal's
 * signature glance, ported in slice 3 of the portal→CC merge —
 * ADR-0067). After session-21 the row IS the Operations nav: the
 * separate dot+label sub-nav strip was removed when live inspection
 * showed two parallel navigation surfaces ("tabs under tabs") plus the
 * row missed Surface + New Leads cards, reading as broken.
 *
 * Why a dedicated endpoint, not array `.length` on the existing book
 * responses: terminal books (Closed, Rejected) ship a recent window
 * by design, so their array lengths *undercount* the full population
 * (the row is a true census of where matters live). The worker counts
 * each phase against its source-of-truth filter (lead status for
 * Under Review / Rejected; placement decision for the placement-band
 * phases; task / intake-group counts for the two non-lifecycle entries)
 * and returns just the numbers — no records, no projections.
 *
 * Counted phases — all 9 Operations books, in pipeline order: the
 * Surface (operator attentions) and New Leads (intake email groups)
 * lead, then the 7 lifecycle phases: the user reads the row as
 * navigation, so every book has an entry. The two non-lifecycle
 * phases carry their own count treatments: Surface = live operator
 * attentions (`TaskReader.countOpenOperations` — Component=Operations,
 * Completed blank); New Leads = unverified intake email groups
 * (composed in the worker against LeadReader's unverified-only filter
 * on Leads — see ADR-0094).
 *
 * Keys: match the `key` on each `OperationsPhase` in apps/web/src/
 * lib/operations-phases.ts so the row component can look up by
 * `phase.key` without an extra mapping.
 */

import { z } from "zod";

const Count = z.number().int().nonnegative();

export const OperationsSummaryResponseSchema = z.object({
  /**
   * Per-phase counts of matters / attentions / groups currently in that
   * phase. Each key is the stable route key from `OPERATIONS_PHASES`
   * (since 2026-10-16, #1142, each key is its label slug — `under-review` is
   * Under Review, `accepted` is Accepted, etc. — so this contract stays
   * robust to label renames).
   */
  counts: z.object({
    /** The Surface — live operator attentions (Tasks with Component=
     *  Operations, Completed blank; org-wide, not viewer-relative). */
    surface: Count,
    /** New Leads — distinct intake email groups since the launch cutoff
     *  that the operator hasn't verified yet (the Intake Verification
     *  queue's length, computed without the per-lead firm/placement
     *  joins the full queue payload carries). */
    "new-leads": Count,
    /** Under Review — leads in the routing band (status = "Under Review"
     *  plus the Under Review MISC limbo statuses; matches the Under Review book). */
    "under-review": Count,
    /** Accepted — placements at Decision = "Accepted" (awaiting the FSA). */
    accepted: Count,
    /** Placed — placements at Decision = "Placed" (signed, active). */
    placed: Count,
    /** Settled — placements at Decision = "Settled" (awaiting disbursement). */
    settled: Count,
    /** Closed — placements at Decision = "Case Closed" (terminal). */
    closed: Count,
    /** Dropped — placements at Decision = "Dropped" (HF terminated). */
    dropped: Count,
    /** Rejected — leads at Status = "Rejected" (terminal; no firm took). */
    rejected: Count,
  }),
});

export type OperationsSummaryResponse = z.infer<
  typeof OperationsSummaryResponseSchema
>;

/**
 * The route keys this contract covers — exported so the row component
 * can iterate `OPERATIONS_PHASES.filter(p => SUMMARY_PHASE_KEYS.has(p.key))`
 * without re-listing them, and a future addition forces a single edit here.
 * Order matches `OPERATIONS_PHASES` (pipeline order — Surface and New Leads
 * lead, then the 7 lifecycle phases).
 */
export const SUMMARY_PHASE_KEYS = [
  "surface",
  "new-leads",
  "under-review",
  "accepted",
  "placed",
  "settled",
  "closed",
  "dropped",
  "rejected",
] as const;

export type SummaryPhaseKey = (typeof SUMMARY_PHASE_KEYS)[number];
